import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { usePage } from '../../context/PageContext';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useEscapeToGoBack } from '../../hooks/useEscapeToGoBack';
import { useFetchWorkTimeTypes } from '../../hooks/useFetchWorkTimeTypes';
import { useInView } from 'react-intersection-observer';
import { IWorkTime, IWorkTimeType } from '../../interface/workTimes';
import { useWorkTime } from '../../context/WorkTimeContext';
import { useEffect, useMemo, useState } from 'react';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { Radio, RadioGroup, Spin, Text } from '../Ant';
import NoData from '../dashboard/noData';
import { GeneralEmpty, Slash } from '../icons';
import { getTranslationFromTranslatableEntity } from '../../constants/matter';
import { useLanguage } from '../../context/LanguageContext';
import SkeletonOptionList from '../common/skeleton/skeletonOptionList';
import SortSelector from '../sortPopover/sortSelector';
import { IActiveSortState, ISortOption } from '../../interface/common';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { useDiscardDialog } from '../../hooks/useDiscardDialog';
import { isWorkTimeFormChanged } from './isWorkTimeFormChanged';
import CustomModal from '../informationPopup/customModal';

const WorkTimeTypes = () => {
  const { t } = useTranslation();
  const { setTitle } = usePage();
  const { goBack, goBackWithSelection } = useNavigationStack();
  const { getAppLanguageCode } = useLanguage();
  const { workTime, updateWorkTime, initialWorkTime } = useWorkTime();
  const { showDiscardDialog, promptDiscardIfChanged, handleDiscardConfirm, handleDiscardCancel } =
    useDiscardDialog();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const {
    data: workTimeTypesList,
    isLoading: isWorkTimeTypesLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchWorkTimeTypes({
    limit: 50,
    search: searchValue,
  });
  const handleRefreshWorkTimeTypes = async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ['work-time-types'] });
    } finally {
      setIsRefreshing(false);
    }
  };
  const showSkeleton = isWorkTimeTypesLoading || isRefreshing;
  const handleBack = () => {
    goBack();
  };
  useEscapeToGoBack(handleBack);
  const handleClose = () => {
    promptDiscardIfChanged(isWorkTimeFormChanged(workTime, initialWorkTime, !!workTime?.id), () => {
      updateWorkTime(null);
      goBack(undefined, 2);
    });
  };
  const appLanguage = getAppLanguageCode();
  const workTimeTypeSortOptions: ISortOption[] = useMemo(
    () => [
      { field: 'name', label: t('worktime:workTimeType'), type: 'text', defaultDirection: 'ASC' },
    ],
    [t],
  );
  const [workTimeTypeActiveSort, setWorkTimeTypeActiveSort] = useState<IActiveSortState>({
    field: 'name',
    direction: 'ASC',
  });
  const sortedWorkTimeTypesList = useMemo(
    () =>
      workTimeTypesList
        ? [...workTimeTypesList].sort((a, b) => {
            const comparison = getTranslationFromTranslatableEntity(
              a.translations,
              appLanguage,
            ).localeCompare(
              getTranslationFromTranslatableEntity(b.translations, appLanguage),
              appLanguage,
            );
            return workTimeTypeActiveSort.direction === 'DESC' ? -comparison : comparison;
          })
        : workTimeTypesList,
    [workTimeTypesList, appLanguage, workTimeTypeActiveSort.direction],
  );
  const { ref, inView } = useInView({
    threshold: 0.5, // Trigger when 50% of the target is visible
  });
  const handleAddWorkTimeType = (type: IWorkTimeType) => {
    const workTimeTypeTranslation = getTranslationFromTranslatableEntity(
      type?.translations,
      getAppLanguageCode(),
    );
    const hasSubject = Boolean(workTime?.subject && workTime.subject.trim() !== '');
    const shouldSetSubject = !workTime?.isSubjectManuallyEdited || !hasSubject;
    const payload = {
      ...workTime,
      workTimeType: type,
      ...(shouldSetSubject
        ? {
            subject: workTimeTypeTranslation || workTime?.subject || '',
            isSubjectManuallyEdited: false,
          }
        : {}),
    } as IWorkTime;
    updateWorkTime(payload);
    goBackWithSelection();
  };
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && workTimeTypesList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={handleBack}
        handleClose={handleClose}
        showBackWithCross
        title={t('worktime:selectWorkTimeType')}
        showCross
      />,
    );
  }, []);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin data-testid="small-indicator" size="small" />;
    }
    return null;
  };
  return (
    <>
      <CollapsibleListLayout
        testId="work-time-types"
        header={
          <SearchInputWithFilters
            searchTitle={t('worktime:searchWorkTimeTypes')}
            searchValue={searchValue}
            handleSearch={setSearchValue}
            handleRefresh={handleRefreshWorkTimeTypes}
          >
            <SortSelector
              modalLabel={t('common:sorting.title')}
              options={workTimeTypeSortOptions}
              activeSort={workTimeTypeActiveSort}
              onApply={setWorkTimeTypeActiveSort}
            />
          </SearchInputWithFilters>
        }
      >
        {!showSkeleton && sortedWorkTimeTypesList && sortedWorkTimeTypesList?.length > 0 && (
          <RadioGroup
            data-testid="radio-group"
            className="d-flex fd-column gap-10"
            value={workTime?.workTimeType?.id}
          >
            {sortedWorkTimeTypesList?.map((type: IWorkTimeType) => (
              <Radio
                key={type?.id}
                value={type?.id}
                onClick={() => {
                  const selectedItem = sortedWorkTimeTypesList?.find(
                    (item) => item.id === type?.id,
                  );
                  handleAddWorkTimeType(selectedItem as IWorkTimeType);
                }}
              >
                <Text $fontsize="13px" $color="title" $weight={500} $lineheight="20px">
                  {getTranslationFromTranslatableEntity(type?.translations, getAppLanguageCode())}
                </Text>
              </Radio>
            ))}
          </RadioGroup>
        )}
        {hasNextPage && <div ref={ref} className="loading-indicator" />}
        {!showSkeleton &&
          (!sortedWorkTimeTypesList || Object.entries(sortedWorkTimeTypesList).length === 0) && (
            <div className="flex-grow d-flex justify-center items-center">
              <NoData
                header={t('transaction:noWorkTimeTypes')}
                subHeader={t('common:tryChangingSearchCriteria')}
                icon={<GeneralEmpty />}
              />
            </div>
          )}
        {showSkeleton ? <SkeletonOptionList /> : renderLoader()}
      </CollapsibleListLayout>
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
    </>
  );
};

export default WorkTimeTypes;
